import axios from 'axios';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  postMarkManual,
  useAdminPayoutsCache,
  type MarkManualVariables,
} from '../../../api/admin-payouts';
import { publishToast } from '../../../components/feedback';

/**
 * Encapsulates the mark-manually-settled orchestration for the payout
 * detail drawer (AUT-128): fires the destructive POST, a success / error
 * toast, and invalidates the `['admin','payouts']` cache so the list +
 * drawer refetch. Extracted from the drawer so the body stays under the
 * line cap. The action is role + permission (`process_payouts`) + audit
 * gated server-side (AUT-138 removed per-action TOTP step-up).
 *
 * The required `note` is collected by `MarkManualDialog` (RHF + Zod, max
 * 500) BEFORE the POST — we never POST an empty note (architect guardrail
 * #6).
 *
 * 🟠 #7 INVALIDATE-ONLY. The mark-manual response is the LIST-shape
 * `{dto, previousStatus}` envelope — NOT used to patch the drawer.
 *
 * 🟠 #4 The 409 `payout_status_stale` (concurrent admin / notifier race)
 * is auto-toasted by the shared interceptor — but we STILL `invalidate`
 * on it so the stale `markManualEligible` flag re-syncs. A non-409
 * rejection falls through to our generic error toast.
 */

/** The 409 stale-state code the backend emits when the FSM moved under us. */
const STALE_CODE = 'payout_status_stale';

export interface UseMarkManualResult {
  /** True while the mutation is in flight. */
  busy: boolean;
  /** Run a mark-manual; resolves true on success, false on error. */
  run: (variables: MarkManualVariables) => Promise<boolean>;
}

export const useMarkManual = (): UseMarkManualResult => {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const { invalidate } = useAdminPayoutsCache();

  const run = async (variables: MarkManualVariables): Promise<boolean> => {
    setBusy(true);
    try {
      await postMarkManual(variables);
      await invalidate();
      publishToast({
        severity: 'success',
        durationMs: 4000,
        message: t('payouts.markManual.successToast'),
      });
      return true;
    } catch (err: unknown) {
      // 🟠 #4 — the stale flag may have moved; refresh regardless of code.
      await invalidate();
      // The 409 stale conflict already surfaced via the shared interceptor;
      // suppress our duplicate toast for it. Everything else → generic.
      if (axios.isAxiosError(err)) {
        const code = (err.response?.data as { code?: string } | undefined)?.code;
        if (code === STALE_CODE) return false;
      }
      publishToast({
        severity: 'error',
        durationMs: 6000,
        message: t('payouts.markManual.errorToast'),
      });
      return false;
    } finally {
      setBusy(false);
    }
  };

  return { busy, run };
};
